import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { CartEmpty } from "@faire/slate-icons/CartEmpty";
import { Avatar } from "@faire/slate/components/avatar";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";
import { cn } from "../../lib/utils";

import type { BrandSideCartItem } from "./BrandSideCart";
import type { GlobalSideCartBrand } from "./GlobalSideCartBrandList";

export const GLOBAL_SIDE_CART_PANEL_ID = "global-side-cart-panel";

export interface GlobalSideCartMiniViewProps {
  brands: GlobalSideCartBrand[];
  /** Currently open individual brand cart, or null for the multi-brand list. */
  selectedBrand: string | null;
  isExpanded: boolean;
  onExpand: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Collapsed mini-rail contents from the global side cart PoC: a cart icon with
 * a brand-count badge, then either one avatar per brand cart or (when a brand
 * is selected) that brand's avatar plus product thumbnails.
 */
export default function GlobalSideCartMiniView({
  brands,
  selectedBrand,
  isExpanded,
  onExpand,
  onMouseEnter,
  onMouseLeave,
}: GlobalSideCartMiniViewProps) {
  const cartLabel = usePlaygroundLocalMsg("Cart");
  const selected = brands.find((brand) => brand.brandName === selectedBrand);

  return (
    <aside
      className="bg-fs-surface-primary border-fs-border-muted hover:bg-fs-surface-secondary active:bg-fs-surface-tertiary flex h-full w-16 flex-col overflow-hidden border-l"
      aria-label={cartLabel}
      aria-hidden={isExpanded}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        {isExpanded ? null : (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={GLOBAL_SIDE_CART_PANEL_ID}
            aria-label={cartLabel}
            className="absolute inset-0 z-1 cursor-pointer border-0 bg-transparent p-0"
            onClick={onExpand}
          />
        )}
        <div className="relative flex min-h-0 flex-1 flex-col" aria-hidden>
          <MiniCartIcon count={brands.length} />
          {selected ? (
            <MiniBrandDetail brand={selected} />
          ) : (
            <MiniBrandList brands={brands} />
          )}
        </div>
      </div>
    </aside>
  );
}

const MiniCartIcon = ({ count }: { count: number }) => (
  <div className="flex shrink-0 justify-center pt-4 pb-2">
    <div className="relative">
      <CartEmpty width="25px" height="25px" />
      {count > 0 ? (
        <MiniCountBadge
          count={count > 99 ? "99+" : String(count)}
          className="absolute -top-2 -right-3"
        />
      ) : null}
    </div>
  </div>
);

const MiniBrandList = ({ brands }: { brands: GlobalSideCartBrand[] }) => (
  <ul className="flex min-h-0 flex-1 flex-col items-center gap-3 overflow-x-hidden overflow-y-auto p-3">
    {brands.map((brand) => (
      <li key={brand.brandName}>
        <MiniAvatar
          imageUrl={brand.items[0]?.imageUrl}
          label={brand.brandName}
        />
      </li>
    ))}
  </ul>
);

const MiniBrandDetail = ({ brand }: { brand: GlobalSideCartBrand }) => (
  <div className="flex min-h-0 flex-1 flex-col items-center gap-3 overflow-x-hidden overflow-y-auto p-3">
    <MiniAvatar
      imageUrl={brand.items[0]?.imageUrl}
      label={brand.brandName}
    />
    <div className="border-fs-border-muted w-full border-b" />
    {brand.items.map((item) => (
      <ProductAvatar key={item.token} item={item} />
    ))}
  </div>
);

const MiniAvatar = ({
  imageUrl,
  label,
}: {
  imageUrl?: string;
  label: string;
}) => (
  <div className="shrink-0" aria-label={label}>
    <Avatar
      image={imageUrl !== undefined ? { url: imageUrl } : undefined}
      label={label}
      size="xSmall"
    />
  </div>
);

// Products are square with a slight border radius, distinct from the
// circular brand Avatar above — Slate's Avatar is circle-only, so this uses a
// plain thumbnail matching the size-10 (40px) "xSmall" Avatar footprint.
const ProductAvatar = ({ item }: { item: BrandSideCartItem }) => {
  const { src, onError } = usePlaygroundImageWithFallback(item.imageUrl);

  return (
    <picture className="block size-10 shrink-0 overflow-hidden rounded-md">
      <img
        src={src}
        onError={onError}
        alt={item.name}
        referrerPolicy="no-referrer"
        className="h-full w-full object-cover"
      />
    </picture>
  );
};

const MiniCountBadge = ({
  count,
  className,
}: {
  count: string;
  className?: string;
}) => (
  <span
    className={cn(
      "bg-fs-surface-primary-inverse text-fs-text-primary-inverse border-fs-surface-primary type-fs-label-medium flex h-5 min-w-5 items-center justify-center rounded-full border-2 px-1",
      className
    )}
  >
    {count}
  </span>
);
